import { useCallback, useEffect, useState } from 'react';

/**
 * Dashboard widget registry.
 *
 * Each widget has:
 *   id          stable, used as localStorage key
 *   label       human-readable name
 *   description short helper text (shown in Settings)
 *   defaultEnabled  initial visibility
 *   size        'sm' | 'md' | 'lg' | 'xl' | 'full'
 *                 → controls min-width via Tailwind basis on flex-wrap layout.
 *                 → all sizes naturally wrap to next row on smaller screens.
 *
 * Adding a widget? Add an entry below and a matching `case` in
 * DashboardPage's `renderWidget()` switch.
 */
export const DASHBOARD_WIDGETS = [
  { id: 'kpi_tracks',     label: 'Total Tracks',      description: 'Library size at a glance',                   defaultEnabled: true,  size: 'sm' },
  { id: 'kpi_crates',     label: 'Total Crates',      description: 'How many crates you have curated',           defaultEnabled: true,  size: 'sm' },
  { id: 'kpi_sets',       label: 'Total Sets',        description: 'AI-generated + manual sets count',           defaultEnabled: true,  size: 'sm' },
  { id: 'kpi_playtime',   label: 'Total Playtime',    description: 'Aggregate length of all your sets',          defaultEnabled: true,  size: 'sm' },
  { id: 'kpi_keymatch',   label: 'Key Match Score',   description: 'Harmonic compatibility across your sets',    defaultEnabled: true,  size: 'sm' },
  { id: 'latest_ai_set',  label: 'Latest AI Set',     description: 'Your most recent AI-built set with player',  defaultEnabled: true,  size: 'full' },
  { id: 'energy_flow',    label: 'Energy Flow',       description: 'Per-track energy curve across the set',      defaultEnabled: true,  size: 'lg' },
  { id: 'harmonic_map',   label: 'Harmonic Map',      description: 'Camelot-wheel position scan of the set',     defaultEnabled: true,  size: 'md' },
  { id: 'set_insights',   label: 'Set Insights',      description: 'BPM range, dominant key, vibe summary',      defaultEnabled: true,  size: 'md' },
  { id: 'transition_card',label: 'Next Transition',   description: 'Suggested next track for the currently playing one', defaultEnabled: true, size: 'md' },
];

const STORAGE_KEY = 'cratelab_dashboard_widgets_v1';

function readState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function defaultState() {
  const enabled = {};
  for (const w of DASHBOARD_WIDGETS) enabled[w.id] = w.defaultEnabled;
  return { enabled, order: DASHBOARD_WIDGETS.map((w) => w.id) };
}

function mergeState(stored) {
  // Forward-compat: if we add a new widget id later, fold it into stored state.
  const base = defaultState();
  if (!stored) return base;
  const enabled = { ...base.enabled };
  for (const k of Object.keys(stored.enabled || {})) {
    if (k in enabled) enabled[k] = !!stored.enabled[k];
  }
  const knownIds = new Set(DASHBOARD_WIDGETS.map((w) => w.id));
  const order = Array.isArray(stored.order)
    ? stored.order.filter((id) => knownIds.has(id))
    : base.order;
  // Append any new widgets at the end of the order list
  for (const id of base.order) {
    if (!order.includes(id)) order.push(id);
  }
  return { enabled, order };
}

export function useDashboardWidgets() {
  const [state, setState] = useState(() => mergeState(readState()));

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore — private mode etc.
    }
    // Notify other open tabs / components (Settings + Dashboard sync)
    window.dispatchEvent(new CustomEvent('cratelab:widgets-changed'));
  }, [state]);

  // Cross-page sync — Settings toggles update Dashboard live
  useEffect(() => {
    const onChange = () => setState(mergeState(readState()));
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setState(mergeState(readState())); };
    window.addEventListener('cratelab:widgets-changed', onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('cratelab:widgets-changed', onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const toggle = useCallback((id) => {
    setState((s) => ({ ...s, enabled: { ...s.enabled, [id]: !s.enabled[id] } }));
  }, []);

  const reset = useCallback(() => setState(defaultState()), []);

  const setEnabled = useCallback((id, on) => {
    setState((s) => ({ ...s, enabled: { ...s.enabled, [id]: !!on } }));
  }, []);

  return {
    state,
    isEnabled: (id) => !!state.enabled[id],
    toggle,
    setEnabled,
    reset,
    visibleWidgets: state.order
      .map((id) => DASHBOARD_WIDGETS.find((w) => w.id === id))
      .filter((w) => w && state.enabled[w.id]),
    allWidgets: DASHBOARD_WIDGETS,
  };
}

/**
 * Tailwind class map per widget size — designed to live inside a parent
 * `flex flex-wrap gap-4` container. Each widget gets a `basis-*` minimum
 * so it falls onto a new row when squashed below threshold.
 */
export const SIZE_CLASSES = {
  sm:   'basis-full min-w-0',
  md:   'basis-full min-w-0',
  lg:   'basis-full min-w-0',
  xl:   'basis-full min-w-0',
  full: 'basis-full min-w-0',
};
